import { useState } from "react";
import BeatOutstanding from "@/components/common/BeatOutstanding";
import { Link, useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ArrowLeftRight, CalendarX, HandCoins, Undo2 } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { inr, num, fmtDate } from "@/lib/format";
import { Kpi, Loading, PageHeader, Section, StatusBadge } from "@/components/common/kit";
import DataTable from "@/components/common/DataTable";
import { DueReport } from "@/pages/v2/Credit";

export const OVERVIEW_COLS = [
  { key: "full_name", label: "Salesman" },
  { key: "opening", label: "Opening", type: "int" },
  { key: "issued", label: "Issued", type: "int" },
  { key: "sold", label: "Sold", type: "int" },
  { key: "closing", label: "Closing", type: "int" },
  { key: "sales_value", label: "Sales ₹", type: "money" },
  { key: "cash", label: "Cash", type: "money" },
  { key: "gpay", label: "GPay", type: "money" },
  { key: "receivable", label: "Receivable", type: "money" },
  { key: "expenses", label: "Expense", type: "money" },
  { key: "status", label: "Day Status", render: (r) => <StatusBadge status={r.status} /> },
];

function Group({ title, children }) {
  return (
    <div>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">{title}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

function Control({ icon: Icon, label, value, to, alert, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`flex items-center justify-between rounded border px-3 py-2.5 text-sm transition-colors hover:bg-slate-50 ${alert ? "border-amber-300 bg-amber-50/60" : "border-slate-200"}`}>
      <span className="flex items-center gap-2 text-slate-700"><Icon className="h-4 w-4" /> {label}</span>
      <span className="font-mono font-bold">{value}</span>
    </Link>
  );
}

function V2Cards({ date }) {
  const { data: v } = useFetch("/dashboard/v2", { date });
  if (!v) return null;
  const L = ({ to, ...p }) => <Link to={to}><Kpi {...p} /></Link>;
  return (
    <div className="space-y-6" data-testid="v2-cards">
      <Group title="Inventory">
        <L to="/admin/inventory" label="Cigarette warehouse (packs)" value={num(v.inventory.cig_stock)} testid="v2-cig-stock" />
        <L to="/admin/inventory" label="Warehouse 2 (base units)" value={num(v.inventory.other_stock)} testid="v2-other-stock" />
        <L to="/admin/inventory" label="Retail issued / Wholesale deducted" value={`${num(v.inventory.retail_issued)} / ${num(v.inventory.wholesale_deducted)}`} tone="blue" />
        <L to="/admin/inventory" label="Low stock alerts" value={v.inventory.low_stock} tone={v.inventory.low_stock ? "red" : "default"} />
        <L to="/admin/inventory?tab=closing" label="Physical variances" value={v.inventory.variances} tone="amber" />
        <L to="/admin/issue" label="Issue corrections today" value={v.inventory.issue_corrections} />
      </Group>
      <Group title="Wholesale">
        <L to="/admin/wholesale?tab=orders" label="Orders today / awaiting billing" value={`${v.wholesale.orders_today} / ${v.wholesale.awaiting_billing}`} testid="v2-orders" />
        <L to="/admin/wholesale?tab=bills" label="Bills finalized today" value={`${v.wholesale.bills_today} · ${inr(v.wholesale.billed_value)}`} tone="green" testid="v2-bills" />
        <L to="/admin/wholesale?tab=loading" label="Pending loading / unfulfilled qty" value={`${v.wholesale.pending_loading} / ${num(v.wholesale.unfulfilled_qty)}`} tone="amber" />
      </Group>
      <Group title="Credit & Collections">
        <L to="/admin/credit?tab=outstanding" label="Customer outstanding" value={inr(v.credit.total)} sub={`Wholesale ${inr(v.credit.wholesale)} · FMCG ${inr(v.credit.fmcg)}`} tone="red" testid="v2-outstanding" />
        <L to="/admin/payments" label="Assigned today / Collected today" value={`${inr(v.credit.assigned_today)} / ${inr(v.credit.collected_today)}`} tone="blue" />
        <L to="/admin/payments" label="Pending approvals / cash to hand over" value={`${v.credit.pending_approvals} / ${inr(v.credit.pending_handover)}`} tone="amber" testid="v2-pending" />
      </Group>
      <Group title="Cashbook">
        <L to="/admin/cashbook" label="Opening / In / Out" value={`${inr(v.cashbook.opening_cash)} / ${inr(v.cashbook.office_cash_in)} / ${inr(v.cashbook.office_cash_out)}`} />
        <L to="/admin/cashbook" label="Expected / Actual closing" value={`${inr(v.cashbook.expected_closing)} / ${inr(v.cashbook.actual_closing)}`} sub={v.cashbook.variance ? `Variance ${inr(v.cashbook.variance)}` : ""} tone="green" testid="v2-cash" />
        <L to="/admin/cashbook" label="UPI in / out · Held by salesmen" value={`${inr(v.cashbook.upi_in)} / ${inr(v.cashbook.upi_out)} · ${inr(v.cashbook.custody_total)}`} tone="blue" />
      </Group>
    </div>
  );
}

export default function Dashboard() {
  const { bizDate } = useAuth();
  const [date, setDate] = useState(bizDate);
  const nav = useNavigate();
  const { data: d } = useFetch("/dashboard/admin", { date });
  if (!d) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" subtitle={`Operations snapshot for ${fmtDate(d.date)}`}
        actions={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="dashboard-date" />} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Group title="Sales">
            <Kpi label="Total Packs Sold" value={num(d.packs_sold)} tone="blue" testid="kpi-packs-sold" />
            <Kpi label="Total Sales Value" value={inr(d.sales_value)} tone="green" testid="kpi-sales-value" />
            <Kpi label="Days Closed" value={`${d.closed_days} / ${d.active_salesmen}`} testid="kpi-days-closed" />
          </Group>
          <Group title="Salesman Stock">
            <Kpi label="Packs with Salesmen" value={num(d.stock_with_salesmen)} testid="kpi-stock-with-salesmen" />
            <Kpi label="Stock Issued Today" value={num(d.issued_today)} tone="blue" testid="kpi-issued-today" />
            <Kpi label="Transfers Pending / Done" value={`${d.transfers_pending} / ${d.transfers_completed}`} tone="amber" testid="kpi-transfers" />
          </Group>
          <Group title="Collections & Expenses">
            <Kpi label="Total Cash" value={inr(d.cash)} tone="green" testid="kpi-cash" />
            <Kpi label="Total GPay / UPI" value={inr(d.gpay)} tone="blue" testid="kpi-gpay" />
            <Kpi label="Total Expenses" value={inr(d.expenses)} tone="amber" testid="kpi-expenses" />
          </Group>
        </div>
        <Section title="Control Items" testid="control-items">
          <div className="space-y-2">
            <Control icon={AlertTriangle} label="Stock shortages today" value={`${d.shortage_count} (${d.shortage_packs} pks)`} to="/admin/adjustments" alert={d.shortage_count > 0} testid="control-shortages" />
            <Control icon={CalendarX} label="Unclosed days" value={d.unclosed_days} to="/admin/closures" alert={d.unclosed_days > 0} testid="control-unclosed" />
            <Control icon={ArrowLeftRight} label="Pending transfers" value={d.transfers_pending} to="/admin/transfers" alert={d.transfers_pending > 0} testid="control-transfers" />
            <Control icon={Undo2} label="Pending returns" value={d.returns_pending} to="/admin/returns" alert={d.returns_pending > 0} testid="control-returns" />
            <Control icon={HandCoins} label="Salesman receivables" value={inr(d.receivables_outstanding)} to="/admin/receivables" alert={d.receivables_outstanding > 0} testid="control-receivables" />
          </div>
          <div className="mt-5 h-40">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Closed sales · last 7 days</p>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(8)} fontSize={10} />
                <YAxis fontSize={10} width={40} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="sales_value" fill="#2563EB" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>
      <V2Cards date={d.date} />
      <DueReport />
      <BeatOutstanding compact />
      <Section title="Salesman Overview">
        <DataTable columns={OVERVIEW_COLS} rows={d.rows} totals={["opening", "issued", "sold", "closing", "sales_value", "cash", "gpay", "receivable", "expenses"]}
          onRowClick={(r) => nav(`/admin/day/${r.salesman_id}/${d.date}`)} title={`Salesman Overview ${d.date}`} exportable testid="dashboard-overview-table" />
      </Section>
    </div>
  );
}
